import React from 'react';

export interface SparklineProps {
  /** Net-per-day series (chronological). */
  values: number[];
  width?: number;
  height?: number;
  /** Accessible label, e.g. "cash net per day". */
  label?: string;
}

/**
 * Dependency-free SVG bar sparkline for a net-per-day series (#572). Bars above
 * the zero baseline (positive net) render green, below render red; magnitudes
 * scale to the largest absolute value in the series. Used by the economy
 * overview to show net-generation trend per resource without a chart library.
 */
export function Sparkline({ values, width = 132, height = 28, label }: SparklineProps) {
  if (values.length === 0) {
    return (
      <span className="text-xs text-ink-low" aria-label={label}>
        —
      </span>
    );
  }

  const max = Math.max(1, ...values.map((v) => Math.abs(v)));
  const n = values.length;
  const gap = n > 1 ? 1 : 0;
  const barW = Math.max(1, (width - gap * (n - 1)) / n);
  const mid = height / 2;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={label ?? 'net per day'}
      data-testid="sparkline"
    >
      {/* Zero baseline */}
      <line x1={0} y1={mid} x2={width} y2={mid} stroke="currentColor" strokeOpacity={0.15} />
      {values.map((v, i) => {
        const h = (Math.abs(v) / max) * (mid - 1);
        const x = i * (barW + gap);
        const y = v >= 0 ? mid - h : mid;
        return (
          <rect
            key={i}
            x={x}
            y={y}
            width={barW}
            height={Math.max(0.5, h)}
            className={v >= 0 ? 'fill-green-600' : 'fill-red-600'}
          />
        );
      })}
    </svg>
  );
}
